import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Microphone, Play, SpeakerHigh, FloppyDisk, CheckCircle, Warning,
  ChatCircleDots, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * VoiceSettingsBlock — reusable "Respostas por áudio" section.
 *
 * Plugged into pages where the operator authors content that the AI later
 * sends (Welcome message, Message templates, Automatic rules, etc.).
 *
 * It reads/writes /api/message/settings (tenant-wide audio config) and
 * /api/message/audio/{status,voices,preview}. The page hosting this block
 * does NOT need to manage any audio state — just drop it in.
 *
 * Props:
 *   - moments         (array): which audio moments to display (defaults to all 8)
 *   - intro           (string): contextual one-liner shown under the title
 *   - testidPrefix    (string): prefix for data-testids so multiple instances
 *                                on the same page don't collide
 */
const ALL_TONES = [
  { v: "acolhedor",  l: "Acolhedor",  d: "Caloroso, próximo, atendimento humanizado." },
  { v: "consultivo", l: "Consultivo", d: "Pondera e orienta — consultor experiente." },
  { v: "premium",    l: "Premium",    d: "Discreto, sofisticado, plural majestático." },
  { v: "direto",     l: "Direto",     d: "Objetivo, sem rodeios." },
];

const ALL_MOMENT_LABELS = {
  first_contact:     "Primeiro contato (lead novo)",
  reactivation:      "Cliente inativo (reativação)",
  post_service:      "Pós-atendimento",
  appointment:       "Agendamento (confirmação / lembrete)",
  vip:               "Cliente VIP",
  budget_recovery:   "Recuperação de orçamento",
  important_confirm: "Confirmação importante",
  manual:            "Mensagem manual do operador",
};

export default function VoiceSettingsBlock({
  moments = null, // null → show all
  intro = "Quando habilitado, o AgentUS pode mandar respostas em áudio humanizado pelos momentos marcados. Se algo falhar, cai pra texto.",
  testidPrefix = "voice",
  defaultExampleText = "Olá Rafael, tudo bem? Vi aqui que faz um tempinho desde a última revisão do seu carro. Se quiser, eu já consigo verificar um horário pra essa semana.",
}) {
  const [status, setStatus] = useState(null);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [settings, setSettings] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewText, setPreviewText] = useState(defaultExampleText);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get("/message/settings"),
          api.get("/message/audio/status").catch(() => ({ data: null })),
        ]);
        setSettings(s.data); setOriginal(s.data); setStatus(st.data);
      } catch {
        toast.error("Não consegui carregar as configurações de voz.");
      }
    })();
  }, []);

  if (!settings) return null;

  const slice = (s) => ({
    audio_enabled: s.audio_enabled,
    audio_only_special: s.audio_only_special,
    audio_moments: s.audio_moments,
    audio_voice_id: s.audio_voice_id,
    audio_model_id: s.audio_model_id,
    audio_tone: s.audio_tone,
    audio_daily_limit: s.audio_daily_limit,
    audio_consume_credits: s.audio_consume_credits,
  });
  const dirty = JSON.stringify(slice(settings)) !== JSON.stringify(slice(original));

  const isReady = status?.available;
  const visibleMomentLabels = moments
    ? Object.fromEntries(moments.map((m) => [m, ALL_MOMENT_LABELS[m] || m]))
    : ALL_MOMENT_LABELS;

  // Only PT-BR voices in the picker
  const ptVoices = voices
    .filter((v) => (v.labels?.language || "").toLowerCase() === "pt")
    .sort((a, b) => a.name.localeCompare(b.name, "pt"));
  const femaleVoices = ptVoices.filter((v) => (v.labels?.gender || "").toLowerCase() === "female");
  const maleVoices = ptVoices.filter((v) => (v.labels?.gender || "").toLowerCase() === "male");
  const otherGender = ptVoices.filter((v) => !["male", "female"].includes((v.labels?.gender || "").toLowerCase()));

  const loadVoices = async () => {
    if (voices.length > 0 || loadingVoices) return;
    setLoadingVoices(true);
    try {
      const { data } = await api.get("/message/audio/voices");
      setVoices(data?.voices || []);
    } catch {
      toast.error("Falha ao carregar vozes — confira a API key da ElevenLabs.");
    } finally { setLoadingVoices(false); }
  };

  const toggleMoment = (code) => {
    const cur = new Set(settings.audio_moments || []);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    setSettings({ ...settings, audio_moments: Array.from(cur) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/message/settings", slice(settings));
      setSettings(data); setOriginal(data);
      toast.success("Configurações de voz salvas.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally { setSaving(false); }
  };

  const preview = async () => {
    if (!previewText.trim()) return;
    setPreviewLoading(true); setPreviewUrl("");
    try {
      const { data } = await api.post("/message/audio/preview", {
        text: previewText,
        voice_id: settings.audio_voice_id || undefined,
        tone: settings.audio_tone || "acolhedor",
      });
      setPreviewUrl(data.audio_url);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 200);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha na geração do áudio";
      toast.error(typeof msg === "string" ? msg : "Falha na geração");
    } finally { setPreviewLoading(false); }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid={`${testidPrefix}-section`}
    >
      <header className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Microphone size={14} weight="fill" className="text-slate-700" />
        <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">Respostas por áudio</h3>
        <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-slate-900 text-white font-bold">CONCIERGE</span>
        {isReady ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-700 font-semibold">
            <CheckCircle size={11} weight="fill" /> conectado
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <Warning size={11} weight="fill" /> indisponível
          </span>
        )}
      </header>

      <div className="p-5 space-y-4">
        <p className="text-[12px] text-slate-500 leading-relaxed">{intro}</p>

        <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50">
          <input
            type="checkbox"
            checked={settings.audio_enabled === true}
            onChange={(e) => setSettings({ ...settings, audio_enabled: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            data-testid={`${testidPrefix}-enabled-toggle`}
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 text-[13.5px]">Habilitar Modo Concierge por Áudio</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">Quando desligado, todas as mensagens saem em texto normal.</div>
          </div>
        </label>

        {settings.audio_enabled && (
          <div className="space-y-4 pt-1">
            {/* Mode */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSettings({ ...settings, audio_only_special: false })}
                data-testid={`${testidPrefix}-mode-always`}
                className={`text-left p-3 rounded-xl border transition-all ${
                  settings.audio_only_special === false
                    ? "border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-900/15"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <SpeakerHigh size={13} weight="fill" />
                  <span className="font-semibold text-[12.5px]">Sempre usar voz</span>
                </div>
                <p className={`text-[11px] leading-snug ${settings.audio_only_special === false ? "text-slate-300" : "text-slate-500"}`}>
                  Toda mensagem da IA vira áudio.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, audio_only_special: true })}
                data-testid={`${testidPrefix}-mode-special`}
                className={`text-left p-3 rounded-xl border transition-all ${
                  settings.audio_only_special !== false
                    ? "border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-900/15"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <Sparkle size={13} weight="fill" />
                  <span className="font-semibold text-[12.5px]">Só momentos especiais</span>
                  <span className={`text-[8px] uppercase font-bold tracking-wider px-1 py-0.5 rounded ${settings.audio_only_special !== false ? "bg-white/20" : "bg-slate-200 text-slate-700"}`}>recomendado</span>
                </div>
                <p className={`text-[11px] leading-snug ${settings.audio_only_special !== false ? "text-slate-300" : "text-slate-500"}`}>
                  Áudio só nos momentos marcados.
                </p>
              </button>
            </div>

            {/* Moments */}
            {settings.audio_only_special !== false && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">Momentos com áudio</div>
                <div className="grid grid-cols-1 gap-1.5">
                  {Object.entries(visibleMomentLabels).map(([code, label]) => {
                    const checked = (settings.audio_moments || []).includes(code);
                    return (
                      <label
                        key={code}
                        className={`flex items-center gap-2 text-[12.5px] cursor-pointer px-3 py-2 rounded-xl border transition-all ${
                          checked ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white hover:border-slate-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMoment(code)}
                          className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                          data-testid={`${testidPrefix}-moment-${code}`}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Voice + Tone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Voz do agente</span>
                <div className="flex items-center gap-2 mt-1.5">
                  <select
                    value={settings.audio_voice_id || ""}
                    onChange={(e) => setSettings({ ...settings, audio_voice_id: e.target.value })}
                    onFocus={loadVoices}
                    className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-[12.5px] bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                    data-testid={`${testidPrefix}-voice-select`}
                  >
                    <option value="">Voz padrão da plataforma</option>
                    {femaleVoices.length > 0 && (
                      <optgroup label="Vozes femininas (PT-BR)">
                        {femaleVoices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.labels?.age ? ` · ${v.labels.age}` : ""}</option>)}
                      </optgroup>
                    )}
                    {maleVoices.length > 0 && (
                      <optgroup label="Vozes masculinas (PT-BR)">
                        {maleVoices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.labels?.age ? ` · ${v.labels.age}` : ""}</option>)}
                      </optgroup>
                    )}
                    {otherGender.length > 0 && (
                      <optgroup label="Outras (PT-BR)">
                        {otherGender.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                  <button
                    type="button" onClick={loadVoices} disabled={loadingVoices}
                    className="shrink-0 text-[10.5px] font-medium text-slate-500 hover:text-slate-900 px-2 py-1 disabled:opacity-50"
                  >
                    {loadingVoices ? "…" : (voices.length > 0 ? "↻" : "carregar")}
                  </button>
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Tom da voz</span>
                <select
                  value={settings.audio_tone || "acolhedor"}
                  onChange={(e) => setSettings({ ...settings, audio_tone: e.target.value })}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-[12.5px] bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 truncate"
                  data-testid={`${testidPrefix}-tone-select`}
                >
                  {ALL_TONES.map((t) => <option key={t.v} value={t.v}>{`${t.l} — ${t.d}`}</option>)}
                </select>
              </label>
            </div>

            {/* Preview */}
            <div className="bg-slate-50/60 border border-slate-200/80 rounded-xl p-3.5 space-y-2.5">
              <div className="flex items-center gap-1.5">
                <SpeakerHigh size={12} weight="fill" className="text-slate-700" />
                <span className="text-[11.5px] font-semibold text-slate-700">Ouvir um exemplo</span>
                <span className="ml-auto text-[10px] text-slate-500">não consome créditos</span>
              </div>
              <textarea
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] bg-white focus:outline-none focus:border-slate-900 resize-none"
                data-testid={`${testidPrefix}-preview-text`}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button" onClick={preview}
                  disabled={previewLoading || !isReady}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-900 text-white text-[11.5px] font-semibold hover:bg-slate-800 disabled:opacity-50"
                  data-testid={`${testidPrefix}-preview-btn`}
                >
                  <Play size={11} weight="fill" />
                  {previewLoading ? "Gerando…" : "Ouvir exemplo"}
                </button>
                {!isReady && <span className="text-[11px] text-slate-500">Provedor desconectado.</span>}
              </div>
              {previewUrl && (
                <audio ref={audioRef} src={previewUrl} controls className="w-full" data-testid={`${testidPrefix}-preview-player`} />
              )}
            </div>
          </div>
        )}
      </div>

      {dirty && (
        <footer className="bg-slate-50/70 border-t border-slate-100 px-5 py-3 flex items-center justify-end gap-2">
          <span className="mr-auto text-[11.5px] text-slate-500">Alterações de voz não salvas</span>
          <button
            onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 disabled:opacity-50 shadow-sm shadow-slate-900/15"
            data-testid={`${testidPrefix}-save`}
          >
            <FloppyDisk size={12} weight="fill" /> {saving ? "Salvando…" : "Salvar voz"}
          </button>
        </footer>
      )}
    </section>
  );
}
